import { Modal, Select, Spin, Button, message } from "antd"
import React, { useState, useCallback, useEffect } from "react"
import Table from "../Table"
import { productColumns } from "../../../utils/TableData/Products/productColumns"
import { productCategories } from "../../../utils/constants"
import Dropdown from "../Dropdown"
import EmptyState from "../EmptyState"
import useProducts from "../../../services/useProduct"
import { translations } from "../../../utils/Translation/staticTranslation"
import DeleteConfirmationModal from "./DeleteConfirmationModal"
import { Product } from "../../../Apis/productApis"
import "../../../styles/Pages/products.css"

const StoreProductViewModal = ({ selectedStore, visible, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [filterStatus, setFilterStatus] = useState(undefined)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [localProducts, setLocalProducts] = useState(null)

  const { products, loading, currentPage, setCurrentPage } = useProducts(
    selectedStore,
    selectedCategory,
    searchTerm,
    filterStatus
  )

  // Sync local products with hook products
  useEffect(() => {
    if (products) {
      setLocalProducts(products)
    }
  }, [products])

  const lang = localStorage.getItem("language") || "ar"
  const productViewModal = translations[lang]?.StorePage?.product_view_modal
  const product = translations[lang]?.productPage
  const filters = translations[lang]?.filters
  const modal = translations[lang]?.modals
  const { common_error_msg } = translations[lang]

  const itemsPerPage = 10
  const displayProducts = localProducts || products
  const start = (currentPage - 1) * itemsPerPage + 1
  const end = Math.min(
    start + displayProducts?.results?.length - 1,
    displayProducts?.count
  )

  const totalPages = displayProducts?.count
    ? Math.ceil(displayProducts.count / itemsPerPage)
    : 0

  const getPageNumbers = () => {
    const pageNumbers = []
    const maxPagesToShow = 5

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      pageNumbers.push(1)

      if (currentPage < 3) {
        pageNumbers.push(2, 3, 4, "...", totalPages)
      } else if (currentPage > totalPages - 2) {
        pageNumbers.push(
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        )
      } else {
        if (currentPage > 3) pageNumbers.push("...")
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1)
        if (currentPage < totalPages - 2) pageNumbers.push("...")
        pageNumbers.push(totalPages)
      }
    }

    return pageNumbers
  }

  const handleDeleteProduct = useCallback(
    async (row) => {
      setDeleteLoading(true)
      const storeId = selectedStore?.id || row?.store
      const payload = {
        store: Number(storeId),
        product_ids: [Number(row?.id)],
      }

      if (!payload.store || isNaN(payload.store)) {
        message.error(common_error_msg?.select_store_first)
        setDeleteLoading(false)
        return
      }

      try {
        const response = await Product.deleteProduct(payload)
        if (response.status === 204) {
          // Update local products state
          setLocalProducts((prevProducts) => {
            if (!prevProducts) return prevProducts
            return {
              ...prevProducts,
              results: prevProducts.results.filter(
                (product) => product.id !== row?.id
              ),
              count: prevProducts.count - 1,
            }
          })
          message.success(
            common_error_msg?.product_deleted_successfully ||
              "Product deleted successfully"
          )
        }
        setDeleteModalVisible(false)
        setProductToDelete(null)
      } catch (error) {
        console.error("Error:", error)
        message.error(
          common_error_msg?.failed_to_delete_product ||
            "Failed to delete product"
        )
      } finally {
        setDeleteLoading(false)
      }
    },
    [selectedStore, common_error_msg]
  )

  const handleDeleteProductModalOpen = useCallback((row) => {
    setProductToDelete(row)
    setDeleteModalVisible(true)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteModalVisible(false)
    setProductToDelete(null)
  }, [])

  const handleDeleteProductConfirmation = useCallback(() => {
    if (productToDelete) {
      handleDeleteProduct(productToDelete)
    }
  }, [productToDelete, handleDeleteProduct])

  return (
    <div>
      <DeleteConfirmationModal
        deleteLoading={deleteLoading}
        visible={deleteModalVisible}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteProductConfirmation}
        title={modal?.confirm_delete_text}
        content={
          <>
            {modal?.description_1}{" "}
            <b>
              {lang === "ar"
                ? productToDelete?.name_arabic
                : productToDelete?.name_english}
            </b>{" "}
            {modal?.description_2}
          </>
        }
      />
      <Modal
        title={productViewModal?.title}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1200}
        height={800}
        className="product-view"
        style={{ overflow: "auto" }}
      >
        <div className="d-flex mb-24" style={{ height: "40px" }}>
          <Dropdown
            selectedCategory={selectedCategory}
            label={filters?.select}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: product?.dropdown_1?.name, value: "Name" },
              { label: product?.dropdown_1?.category, value: "Category" },
            ]}
            onSelect={(value) => setSelectedCategory(value)}
          />

          <div
            style={{
              marginRight: lang === "ar" ? "0px" : "10px",
              marginLeft: lang === "ar" ? "10px" : "0px",
            }}
          >
            {selectedCategory === "Category" ? (
              <>
                <Select
                  style={{
                    width: "100%",
                    minWidth: "249px",
                    marginRight: lang === "ar" ? "0px" : "12px",
                    marginLeft: lang === "ar" ? "12px" : "0px",
                  }}
                  className="product-search"
                  showSearch
                  placeholder={filters?.select_categories}
                  allowClear
                  onChange={(values) => setSearchTerm(values || "")}
                  options={productCategories.map((item) => ({
                    value: item.value,
                    label: lang === "ar" ? item.label_ar : item.label_en,
                  }))}
                />
              </>
            ) : (
              <>
                <input
                  type="search"
                  className="search-store"
                  placeholder={filters?.search}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </>
            )}
          </div>

          <Dropdown
            selectedCategory={filterStatus || filters?.all}
            label={filters?.all}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: product?.dropdown_2?.all, value: "All" },
              { label: product?.dropdown_2?.active, value: "Active" },
              { label: product?.dropdown_2?.inactive, value: "Inactive" },
            ]}
            onSelect={(value) => {
              setFilterStatus(value === "All" ? "" : value)
            }}
          />
        </div>
        <div className="w-100 h-100">
          {loading ? (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
              <Spin size="large" />
            </div>
          ) : displayProducts?.results?.length > 0 ? (
            <>
              <Table
                columns={productColumns}
                data={displayProducts?.results}
                onDelete={handleDeleteProductModalOpen}
              />

              {/* Enhanced Pagination Controls */}
              {displayProducts?.count > 0 && (
                <div className="d-flex justify-content-between mt-4 align-items-center pagination-wrapper">
                  <div className="results-info">
                    <span className="font-medium text-gray-600">
                      {product?.showing}{" "}
                      <span className="font-bold text-gray-800">
                        {start} - {end}
                      </span>{" "}
                      {product?.of}{" "}
                      <span className="font-bold text-gray-800">
                        {displayProducts?.count} {product?.products}
                      </span>
                    </span>
                  </div>

                  <div className="pagination-container">
                    <button
                      onClick={() =>
                        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className={`pagination-nav-btn ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <span className="nav-arrow">
                        {lang === "ar" ? "→" : "←"}{" "}
                      </span>{" "}
                      <span className="nav-text">
                        {product?.previous || "Previous"}
                      </span>
                    </button>

                    <div className="pagination-numbers">
                      {getPageNumbers().map((pageNum, index) => (
                        <React.Fragment key={index}>
                          {pageNum === "..." ? (
                            <span className="pagination-ellipsis">•••</span>
                          ) : (
                            <button
                              className={`pagination-number-btn ${
                                currentPage === pageNum ? "active-page" : ""
                              }`}
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    <button
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        setCurrentPage((prevPage) =>
                          Math.min(prevPage + 1, totalPages)
                        )
                      }
                      className={`pagination-nav-btn ${
                        currentPage >= totalPages ? "disabled" : ""
                      }`}
                    >
                      <span className="nav-text">
                        {product?.next || "Next"}
                      </span>{" "}
                      <span className="nav-arrow">
                        {lang === "ar" ? "←" : "→"}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              title={product?.no_product?.title}
              description={product?.no_product?.selected_store_no_product}
              buttonPath="/products/create-products"
            />
          )}
        </div>
      </Modal>
    </div>
  )
}

export default StoreProductViewModal
